/**
 * Combos y recetas en un carrito (POS y cotización, spec 20). El combo es un bloque de renglones con el mismo grupo:
 * cambiar la medida reemplaza sus renglones (conserva el descuento); quitar o cambiar uno de sus productos lo desarma
 * (los renglones quedan sueltos, sin el descuento del combo); «quitar combo» quita todos sus renglones.
 */
import { formatearCantidad, Money, UNIDAD_MEDIDA_ETIQUETA, type CalculoComboDTO, type ComboEnDocumentoDTO, type ComboEnVentaInput } from '@mg/shared';

export type ComboEnCarrito = ComboEnDocumentoDTO;

/** Lo mínimo que un renglón de carrito necesita para saber de qué combo es. */
export interface ConGrupo {
  combo_grupo?: string | undefined;
}

/** «26 m²», «12,5 m lineales», «3 combos». */
export function textoMedida(k: Pick<ComboEnCarrito, 'tipo' | 'unidad_medida' | 'medida'>): string {
  const m = formatearCantidad(Money.parse(k.medida), 4);
  if (k.tipo === 'fijo') return `${m} ${Money.parse(k.medida).eq('1') ? 'combo' : 'combos'}`;
  return `${m} ${UNIDAD_MEDIDA_ETIQUETA[k.unidad_medida ?? 'unidad']}`;
}

/** La foto del combo para el carrito, a partir de su cálculo. */
export function fotoDeCalculo(c: CalculoComboDTO, grupo: string): ComboEnCarrito {
  return { grupo, combo_id: c.combo_id, combo_version: c.version, codigo: c.codigo, nombre: c.nombre, tipo: c.tipo, unidad_medida: c.unidad_medida, medida: c.medida };
}

/** Lo que va al servidor: solo los combos que todavía tienen renglones en el carrito. */
export function combosParaEnviar(combos: ComboEnCarrito[], lineas: ConGrupo[]): ComboEnVentaInput[] {
  return combos.filter((k) => lineas.some((l) => l.combo_grupo === k.grupo)).map((k) => ({ grupo: k.grupo, combo_id: k.combo_id, medida: k.medida }));
}

/** Los renglones del combo quedan sueltos (sin grupo): el servidor ya no les da el descuento del combo. */
export function desarmarCombo<L extends ConGrupo>(lineas: L[], grupo: string): L[] {
  return lineas.map((l) => {
    if (l.combo_grupo !== grupo) return l;
    const { combo_grupo: _g, ...resto } = l;
    return resto as L;
  });
}

/** Quita todos los renglones del combo. */
export function quitarCombo<L extends ConGrupo>(lineas: L[], grupo: string): L[] {
  return lineas.filter((l) => l.combo_grupo !== grupo);
}

/** Cambia los renglones del combo por los nuevos (otra medida), en el lugar donde estaba el primero; si no estaba, al final. */
export function reemplazarCombo<L extends ConGrupo>(lineas: L[], grupo: string, nuevas: L[]): L[] {
  const primero = lineas.findIndex((l) => l.combo_grupo === grupo);
  const sin = lineas.filter((l) => l.combo_grupo !== grupo);
  if (primero < 0) return [...sin, ...nuevas];
  const antes = lineas.slice(0, primero).filter((l) => l.combo_grupo !== grupo).length;
  return [...sin.slice(0, antes), ...nuevas, ...sin.slice(antes)];
}

/** ¿Es el primer renglón de su combo en el carrito? (ahí va la cabecera del bloque) */
export function esPrimeroDeSuCombo(lineas: ConGrupo[], i: number): boolean {
  const g = lineas[i]?.combo_grupo;
  return !!g && lineas.findIndex((l) => l.combo_grupo === g) === i;
}
